import React, { useRef, useState } from "react";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { showToast } from "Redux/General/Actions";
import { Api } from "Helpers";
// COMPONENTS
import SitePageShell from "Components/WebsiteTheme/SitePageShell";
import SiteSectionHead from "Components/WebsiteTheme/SiteSectionHead";
import SiteButton from "Components/WebsiteTheme/SiteButton";
import { IconMail, IconShield } from "Components/WebsiteTheme/SiteIcons";
import { revealProps } from "Components/WebsiteTheme/Reveal";
import PageHeading from "Components/PageHeading/PageHeading";
import CaptchaWidget from "Components/Commons/Captcha/CaptchaWidget";
import { captchaHeaders } from "Helpers/captcha";
// STYLES
import { ContactUsPageContainer } from "./ContactUs.style";
// ICONS (same social icons as the footer, shown in the site blue)
import { ReactComponent as FacebookIcon } from "../../../Assets/Images/Icons/fb_icon.svg";
import { ReactComponent as TickTockIcon } from "../../../Assets/Images/Icons/tiktok_icon.svg";
import { ReactComponent as InstaIcon } from "../../../Assets/Images/Icons/insta_icon.svg";

import * as yup from "yup";

/* ---------------- Validation Helper ---------------- */
const generateValidationSchema = (inputs) => {
    const shape = {};

    inputs.forEach((input) => {
        const { name, type, validation = {} } = input; // ✅ safe default
        let schema = yup.string();

        if (validation.required) {
            schema = schema.required(`${input.label || name} is required`);
        }

        if (validation.email) {
            schema = schema.email("Invalid email format");
        }

        if (type === "number") {
            schema = yup
                .number()
                .typeError(`${input.label || name} must be a number`);
        }

        shape[name] = schema;
    });

    return yup.object().shape(shape);
};

/* ---------------- Input Fields ---------------- */
const inputs = [
    { label: "First Name", name: "firstName", validation: { required: true } },
    { label: "Last Name", name: "lastName", validation: { required: true } },
    { label: "School Name", name: "schoolName", validation: { required: false } },
    { label: "District Name", name: "districtName", validation: { required: false } },
    { label: "Email Address", name: "email", type: "email", validation: { required: true, email: true } },
    { label: "Phone Number", name: "phone", validation: { required: false } },
    { label: "Message", name: "message", validation: { required: true } },
];

const validationSchema = generateValidationSchema(inputs);

const ContactUsPage = () => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const captchaRef = useRef(null);

    const formik = useFormik({
        initialValues: {
            firstName: "",
            lastName: "",
            schoolName: "",
            districtName: "",
            email: "",
            phone: "",
            message: "",
        },
        validationSchema,
        onSubmit: async (values, { resetForm }) => {
            try {
                setIsLoading(true);

                const captchaToken = await captchaRef.current?.getToken();

                // Convert message to HTML like old page
                let htmlContent =
                    "<p>" + values?.message.trim().replace(/\n/g, "</p><p>") + "</p><br />";

                const payload = {
                    name: values?.firstName?.trim() || "",
                    lastName: values?.lastName?.trim() || "",
                    schoolName: values?.schoolName?.trim() || "",
                    districtName: values?.districtName?.trim() || "",
                    email: values?.email?.trim() || "",
                    phoneNumber: values?.phone?.trim() || "",
                    message: htmlContent,
                };

                const response = await new Api().post("contact-us", {
                    data: payload,
                    headers: captchaHeaders(captchaToken),
                    skipAuth: true,
                    returnError: true,
                });

                if (response.status === 200) {
                    dispatch(showToast("Your message has been sent successfully!"));
                    resetForm();
                }
            } catch (error) {
                console.error("Contact form submission error:", error);
                // API errors (rate limit, failed security check, validation) are already shown by the Api helper.
                if (!error?.data?.message) dispatch(showToast(error?.message || "Something went wrong. Please try again later."));
            } finally {
                setIsLoading(false);
                captchaRef.current?.reset();
            }
        },
    });

    /** One labelled field with its validation message (same order and wording as before). */
    const field = (name, label, placeholder, type = "text") => {
        const error = formik.touched[name] && formik.errors[name];
        const Tag = type === "textarea" ? "textarea" : "input";
        return (
            <div className={`cu_field ${type === "textarea" ? "cu_field--full" : ""}`}>
                <label htmlFor={`contact-${name}`}>{label}</label>
                <Tag
                    id={`contact-${name}`}
                    type={type === "textarea" ? undefined : type}
                    name={name}
                    placeholder={placeholder}
                    onChange={formik.handleChange}
                    value={formik.values[name]}
                    aria-invalid={error ? "true" : undefined}
                />
                <span className="cu_error">{error}</span>
            </div>
        );
    };

    return (
        <SitePageShell className="contact_page">
            <PageHeading
                tone="site"
                title={
                    <>
                        Contact <span className="sm_accent">Us</span>
                    </>
                }
                breadcrumbs={[
                    { label: "Home", path: "/" },
                    { label: "Contact Us" },
                ]}
            />

            <ContactUsPageContainer className="sm_section">
                <div className="sm_container cu_grid">
                    {/* Left: contact details */}
                    <div className="cu_details" {...revealProps("left")}>
                        <SiteSectionHead
                            eyebrow="Quick Contact"
                            title={
                                <>
                                    We’d Love to <span className="sm_accent">Hear From You</span>
                                </>
                            }
                            lead="Have questions, ideas or need support? We’re just a message away."
                        />

                        <ul className="cu_list">
                            <li>
                                <span className="sm_icon sm_icon--green" aria-hidden="true">
                                    <IconMail />
                                </span>
                                <a href="mailto:Info@subjectsmaster.com" title="Mail Us">Info@subjectsmaster.com</a>
                            </li>
                        </ul>

                        <div className="cu_social">
                            <p className="sm_label">Social Media:</p>
                            <ul>
                                <li>
                                    <a href="https://www.facebook.com/subjectsmaster?mibextid=LQQJ4d" target="_blank" rel="noopener noreferrer" title="Facebook" aria-label="Facebook">
                                        <FacebookIcon />
                                    </a>
                                </li>
                                <li>
                                    <a href="https://www.instagram.com/subjectsmaster?igsh=OGQ5ZDc2ODk2ZA==" target="_blank" rel="noopener noreferrer" title="Instagram" aria-label="Instagram">
                                        <InstaIcon />
                                    </a>
                                </li>
                                <li>
                                    <a href="https://www.tiktok.com/@subjectsmaster?_t=8iVpTGlOxLy&_r=1" target="_blank" rel="noopener noreferrer" title="TikTok" aria-label="TikTok">
                                        <TickTockIcon />
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Right: form */}
                    <div className="cu_form_card" {...revealProps("up", 1)}>
                        <form onSubmit={formik.handleSubmit}>
                            <div className="cu_fields">
                                {field("firstName", "First Name", "Enter first name")}
                                {field("lastName", "Last Name", "Enter last name")}
                                {field("schoolName", "School Name", "Enter school name")}
                                {field("districtName", "District Name", "Enter district name")}
                                {field("email", "Email Address", "Enter email address", "email")}
                                {field("phone", "Phone Number", "Enter phone number", "tel")}
                                {field("message", "Message", "Enter message", "textarea")}
                            </div>

                            <CaptchaWidget ref={captchaRef} action="contact_us" />

                            <div className="cu_submit_row">
                                <SiteButton type="submit" disabled={isLoading} aria-busy={isLoading ? "true" : undefined}>
                                    {isLoading ? "Sending..." : "Submit"}
                                </SiteButton>
                                <p className="cu_spam sm_caption">
                                    <IconShield aria-hidden="true" />
                                    <span>We hate spam and promise to keep your email safe.</span>
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
            </ContactUsPageContainer>
        </SitePageShell>
    );
};

export default ContactUsPage;
